/**
 * Innstillinger › Bransje og tariffavtale (#294 ONB-08; prototype onboarding-handoff-2026-11-02
 * «Arbeidstid og regler», screen 07). The administrator names the line of work, whether a collective
 * agreement applies and from when, and confirms it. The profile decides no rule: Timefly keeps the
 * Working Environment Act's limits as they are (ADR 0010), and the sector only suggests what to check.
 * The status is the server's (GET /tenant rule_profile), the same Kom i gang reads. The form is a draft
 * until saved, so a refused save keeps what was typed (ONB-27); a change clears an earlier confirmation.
 */
import { errorText } from '@/api/client';
import { useTenantWrite } from '@/api/company';
import { T } from '@/i18n';
import { breakCalcLabel, modeOf } from '@/rules/breakSetting';
import type { RuleSector, Tenant } from '@/shell/tenant';
import { Button, Chip, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { num } from '@/ui/format';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import ps from './Pause.module.css';
import { SECTORS, missingText, profileLabel, suggestedSector } from './ruleProfile';

type Agreement = '' | 'none' | 'yes';

export default function Regelprofil({ tenant }: { tenant: Tenant }) {
  const p = tenant.rule_profile;
  const suggested = suggestedSector(tenant.nace_code);
  const [sector, setSector] = useState<RuleSector | ''>(p?.sector ?? '');
  const [agreement, setAgreement] = useState<Agreement>(p?.agreement ?? '');
  const [name, setName] = useState(p?.agreement_name ?? '');
  const [from, setFrom] = useState(p?.effective_from ?? '');
  const [confirm, setConfirm] = useState(p?.status === 'confirmed');
  const [error, setError] = useState<string | null>(null);
  const tw = useTenantWrite();
  const chosen = SECTORS.find((s) => s.key === sector);
  const week = tenant.agreed_week_hours;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await tw.mutateAsync({
        id: tenant.id,
        body: {
          rule_sector: sector || null,
          rule_agreement: agreement || null,
          agreement_name: agreement === 'yes' ? name.trim() || null : null,
          rule_effective_from: from || null,
          rule_confirmed: confirm,
          // #267 SET-30: what this page showed; a change by someone else since is refused, not overwritten
          expected: {
            rule_sector: tenant.rule_sector ?? null,
            rule_agreement: tenant.rule_agreement ?? null,
            agreement_name: tenant.agreement_name ?? null,
            rule_effective_from: tenant.rule_effective_from ?? null,
          },
        },
      });
      toast(confirm ? T('Gjennomgangen er lagret og bekreftet.') : T('Lagret. Det som mangler, står igjen.'));
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <form onSubmit={submit} aria-label={T('Bransje og tariffavtale')}>
      <output className={ps.notice}>
        <p>
          <Chip tone={p?.status === 'confirmed' ? 'ok' : p?.status === 'review' ? 'warn' : 'neu'} small>
            {profileLabel(p?.status ?? 'new')}
          </Chip>{' '}
          {p?.status === 'confirmed' && p.confirmed_at
            ? T('Bekreftet {date}.', {
                date: new Date(p.confirmed_at).toLocaleDateString('nb-NO', { timeZone: 'Europe/Oslo' }),
              })
            : p?.missing.length
              ? T('Mangler: {what}.', { what: missingText(p.missing) })
              : T('Alt er fylt ut. Bekreft gjennomgangen for å fullføre steget.')}
        </p>
      </output>

      <h3 className={ps.q}>{T('Hvilket arbeid gjør dere?')}</h3>
      <div className={ps.sub}>
        <label className={ps.row}>
          <span>{T('Bransje')}</span>
          <select
            className="inp"
            value={sector}
            onChange={(e) => setSector(e.target.value as RuleSector | '')}
          >
            <option value="">{T('Velg bransje')}</option>
            {SECTORS.map((s) => (
              <option key={s.key} value={s.key}>
                {T(s.label)}
              </option>
            ))}
          </select>
        </label>
        {suggested && !sector && (
          <p className={ps.apply}>
            {T('Enhetsregisteret oppgir {nace}. Det passer med {sector}.', {
              nace: tenant.nace_text ?? tenant.nace_code ?? '',
              sector: T(SECTORS.find((s) => s.key === suggested)?.label ?? ''),
            })}{' '}
            <button type="button" className="lnk" onClick={() => setSector(suggested)}>
              {T('Bruk forslaget')}
            </button>
          </p>
        )}
        {chosen && (
          <div className={ps.ex}>
            <div className={ps.lab}>{T('Dette bør dere sjekke')}</div>
            <p>{T(chosen.check)}</p>
          </div>
        )}
      </div>

      <h3 className={ps.q} id="rp-agreement">
        {T('Har dere tariffavtale?')}
      </h3>
      <div className={ps.cards} role="radiogroup" aria-labelledby="rp-agreement">
        {(
          [
            ['', T('Vet ikke ennå'), T('Profilen står som ikke avklart til dere vet.')],
            ['none', T('Nei'), T('Ingen tariffavtale gjelder de ansatte.')],
            ['yes', T('Ja'), T('Skriv navnet på den faktiske avtalen, ikke bare bransjen.')],
          ] as [Agreement, string, string][]
        ).map(([v, label, help]) => (
          <label key={v || 'unknown'} className={ps.card}>
            <input
              type="radio"
              name="rp-agreement"
              checked={agreement === v}
              onChange={() => setAgreement(v)}
            />
            <b>{label}</b>
            <span>{help}</span>
          </label>
        ))}
      </div>
      {agreement === 'yes' && (
        <div className={ps.sub}>
          <label className={ps.row}>
            <span>{T('Navn på avtalen')}</span>
            <input className="inp w" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <p className={ps.warn}>
            {T(
              'Timefly legger ikke inn avtalens egne vilkår. Tillegg settes som lønnsarter under Lønn, og arbeidstiden kontrolleres mot arbeidsmiljølovens grenser.',
            )}
          </p>
        </div>
      )}

      <h3 className={ps.q}>{T('Hvem og fra når')}</h3>
      <div className={ps.sub}>
        <div className={ps.row}>
          <span>{T('Gjelder for')}</span>
          {T('Alle ansatte i bedriften')}
        </div>
        <div className={ps.row}>
          <span>{T('Gjelder fra')}</span>
          <DateInput className="inp" aria-label={T('Gjelder fra')} value={from} onValueChange={setFrom} />
        </div>
        <p className={ps.apply}>{T('Datoen lagres med gjennomgangen. Den endrer ingen registrert tid.')}</p>
      </div>

      <div className={ps.ex}>
        <div className={ps.lab}>{T('Dette bruker Timefly for dere i dag')}</div>
        <div>
          <p>{T('Arbeidsmiljølovens grenser for arbeidstid, hvile og pauser, faste i Timefly')}</p>
          <p>
            {T('Avtalt uke')}:{' '}
            {week != null && week !== '' ? `${num(Number(week))} t` : T('ikke satt, 37,5 t brukes')} ·{' '}
            <Link to="/innstillinger/arbeidstid">{T('Endre')}</Link>
          </p>
          <p>
            {T('Pause')}: {breakCalcLabel(modeOf(tenant))} ·{' '}
            <Link to="/innstillinger/pause">{T('Endre')}</Link>
          </p>
          <p>
            {T('Toleranse for stempling')}: {tenant.punch_tolerance_minutes ?? 15} min ·{' '}
            <Link to="/innstillinger/arbeidstid">{T('Endre')}</Link>
          </p>
        </div>
      </div>
      <p className={ps.law}>
        {T(
          'En bransje eller en mal er ikke en bekreftelse på at alt er riktig. Kontroller avtalene som gjelder for dere.',
        )}
      </p>

      <label className="checkline" style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
        <span>{T('Jeg har gjennomgått bransje, tariffavtale og dato')}</span>
      </label>
      {error && (
        <span role="alert" className={ps.err}>
          {error}
        </span>
      )}
      <Button tone="pri" type="submit" disabled={tw.isPending}>
        {T('Lagre gjennomgangen')}
      </Button>
    </form>
  );
}
